import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const translate = (key: string, vars?: Record<string, string | number>) => i18n.t(key, vars);
import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  Route, MapPin, Truck, Loader2, Trash2, AlertTriangle, ChevronDown, ChevronRight,
  Settings2, Plus, Upload, ListChecks, GripVertical, RefreshCw, Search, MousePointerClick, X,
  Coffee, Maximize2, Minimize2, PackageCheck,
} from 'lucide-react';
import { useHasModule } from '../store/modules';
import {
  devicesApi, missionsApi, parcelsApi, vrpApi, geocodingApi,
  type VrpTour, type VrpStop, type VrpOptimizeRequest, type VrpDriver, type ForwardGeocodingResult,
  type Parcel,
} from '../services/api';

const TOUR_COLORS = ['#06b6d4', '#a855f7', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#3b82f6', '#f97316'];
const DEFAULT_CENTER: [number, number] = [2.3522, 48.8566];

type StopSource = 'missions' | 'parcels' | 'csv' | 'manual';

interface ManualStop {
  id: string;
  /** Visible title — also sent as `label` to the optimizer / displayed in the tour stop card. */
  label: string;
  /** Optional dispatcher note appended to the label so the driver sees it on their stop card. */
  description?: string;
  /** Optional phone — appended to the address so it reaches the driver. */
  contactPhone?: string;
  /** Lat/lng resolved silently by the backend forward-geocoder when the user clicks "Ajouter". */
  latitude: number;
  longitude: number;
  /** Composed address shown to the driver — `${street}, ${cp} ${city}, ${country}`. */
  address?: string;
  serviceDurationS?: number;
  twStart?: string;
  twEnd?: string;
  demand?: number;
  requiredSkills?: string[];
}

interface DeviceLite {
  id: string;
  name: string;
  plate: string | null;
  driverId: string | null;
  latitude: number | null;
  longitude: number | null;
  depotLatitude: number | null;
  depotLongitude: number | null;
  capacityKg: number | null;
  skills: string[];
}

/** Driver flattened for the picker — combines driver info + their vehicle's depot/cap/skills. */
interface DriverPickItem {
  driverId: string;
  driverName: string;
  driverEmail: string;
  vehicle: VrpDriver['vehicle'];
  // Effective values — fall back to last known position when no explicit depot
  effectiveDepot: { lat: number | null; lng: number | null };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function decodePolyline(str: string, precision = 5): Array<[number, number]> {
  let index = 0, lat = 0, lng = 0;
  const factor = Math.pow(10, precision);
  const out: Array<[number, number]> = [];
  while (index < str.length) {
    let result = 0, shift = 0, b: number;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    result = 0; shift = 0;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    out.push([lng / factor, lat / factor]);
  }
  return out;
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${m} min`;
}

function formatDistance(meters: number): string {
  return meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${meters} m`;
}

function parseCsv(text: string): { stops: ManualStop[]; errors: string[] } {
  const errors: string[] = [];
  const stops: ManualStop[] = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim() && !l.trim().startsWith('#'));
  for (let i = 0; i < lines.length; i++) {
    const cols = lines[i]!.split(',').map((c) => c.trim());
    if (cols.length < 4) { errors.push(`Ligne ${i + 1}: au moins label,address,lat,lng requis`); continue; }
    const [label, address, latStr, lngStr, svcStr, twS, twE, demStr, skillsStr] = cols;
    if (i === 0 && (Number.isNaN(Number(latStr)) || Number.isNaN(Number(lngStr)))) continue;
    const lat = Number(latStr); const lng = Number(lngStr);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) { errors.push(`Ligne ${i + 1}: lat/lng invalides`); continue; }
    stops.push({
      id: `csv-${i}-${Math.random().toString(36).slice(2, 8)}`,
      label: label || `Stop ${i + 1}`,
      address: address || undefined,
      latitude: lat,
      longitude: lng,
      serviceDurationS: svcStr ? Math.round(Number(svcStr) * 60) : undefined,
      twStart: twS || undefined,
      twEnd: twE || undefined,
      demand: demStr ? Math.max(0, Math.floor(Number(demStr))) : undefined,
      requiredSkills: skillsStr ? skillsStr.split('|').map((s) => s.trim()).filter(Boolean) : undefined,
    });
  }
  return { stops, errors };
}

const STATUS_BADGES: Record<string, string> = {
  DRAFT:       'bg-gray-100 text-gray-700 border-gray-200',
  SCHEDULED:   'bg-blue-50 text-blue-700 border-blue-100',
  IN_PROGRESS: 'bg-amber-50 text-amber-700 border-amber-100',
  COMPLETED:   'bg-emerald-50 text-emerald-700 border-emerald-100',
  CANCELLED:   'bg-red-50 text-red-700 border-red-100',
};

function MissionStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const cls = STATUS_BADGES[status];
  if (!cls) return null;
  return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cls} flex-shrink-0`}>{t(`tour_optim.status_${status}`)}</span>;
}

const inputClass =
  'w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20';

const inputClassSm =
  'w-full px-2 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function TourOptimizationPage() {
  const { t } = useTranslation();
  const hasVrpModule = useHasModule('VRP');
  const hasPod = useHasModule('POD');
  const queryClient = useQueryClient();

  const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime] = useState<string>('08:00');
  const [selectedDriverIds, setSelectedDriverIds] = useState<Set<string>>(new Set());
  const [selectedMissionIds, setSelectedMissionIds] = useState<Set<string>>(new Set());
  const [selectedParcelIds, setSelectedParcelIds] = useState<Set<string>>(new Set());
  const [stopSource, setStopSource] = useState<StopSource>('missions');
  const [csvText, setCsvText] = useState<string>('');
  const [manualStops, setManualStops] = useState<ManualStop[]>([]);
  const [missionOverrides, setMissionOverrides] = useState<Record<string, Partial<ManualStop>>>({});
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [warnMsg, setWarnMsg] = useState<string | null>(null);
  const [openDriverId, setOpenDriverId] = useState<string | null>(null);
  const [openStopId, setOpenStopId] = useState<string | null>(null);
  // When set, the next click on the main map sets the depot of this vehicle.
  const [pickingDepotForVehicleId, setPickingDepotForVehicleId] = useState<string | null>(null);
  const [mapCollapsed, setMapCollapsed] = useState(false);

  const { data: vrpDrivers } = useQuery({
    queryKey: ['vrp-drivers'],
    queryFn: () => vrpApi.listDrivers(),
    enabled: hasVrpModule,
  });
  const drivers = useMemo<DriverPickItem[]>(() => {
    return (vrpDrivers ?? []).map((d) => {
      const v = d.vehicle;
      const explicitLat = v?.depotLatitude ?? null;
      const explicitLng = v?.depotLongitude ?? null;
      const lp = v?.lastPosition;
      return {
        driverId: d.id,
        driverName: `${d.firstName} ${d.lastName}`.trim() || d.email,
        driverEmail: d.email,
        vehicle: v,
        effectiveDepot: {
          lat: explicitLat ?? (typeof lp?.lat === 'number' ? lp.lat : null),
          lng: explicitLng ?? (typeof lp?.lng === 'number' ? lp.lng : null),
        },
      };
    });
  }, [vrpDrivers]);

  const [includeUnscheduled, setIncludeUnscheduled] = useState(false);
  // When at least one driver is checked, hide missions assigned to other
  // drivers. Unassigned missions are included by default so the dispatcher can
  // still drop free-pool work into the optimization.
  const [includeUnassigned, setIncludeUnassigned] = useState(true);

  const { data: missions } = useQuery({
    queryKey: ['vrp-missions', date, includeUnscheduled],
    queryFn: async () => {
      // Cover the entire local day, not the midnight instant alone.
      const dayStart = new Date(`${date}T00:00:00`).toISOString();
      const dayEnd = new Date(`${date}T23:59:59.999`).toISOString();
      const ofDay = await missionsApi.list({ from: dayStart, to: dayEnd, limit: 200 });
      if (!includeUnscheduled) return ofDay;
      // Pull all missions and append the ones with no scheduledAt (server has no
      // dedicated filter for "scheduledAt IS NULL", so we filter client-side).
      const all = await missionsApi.list({ limit: 200 });
      const unscheduled = all.filter((m) => !m.scheduledAt);
      const knownIds = new Set(ofDay.map((m) => m.id));
      return [...ofDay, ...unscheduled.filter((m) => !knownIds.has(m.id))];
    },
    enabled: hasVrpModule,
  });
  // Keep missions that are localizable: either already geocoded, or have at
  // least an address that the backend can forward-geocode at optimize time.
  const geocodedMissions = useMemo(
    () => (missions ?? []).filter((m) => (m.latitude != null && m.longitude != null) || !!m.address),
    [missions],
  );

  // ---- Parcels (POD ↔ VRP) — open parcels, optionally including undated ----
  const { data: parcels } = useQuery({
    queryKey: ['vrp-parcels', date, includeUnscheduled],
    queryFn: async () => {
      const dayStart = new Date(`${date}T00:00:00`).toISOString();
      const dayEnd = new Date(`${date}T23:59:59.999`).toISOString();
      const ofDay = await parcelsApi.list({ from: dayStart, to: dayEnd, limit: 300 });
      const open = (list: Parcel[]) => list.filter((p) => ['CREATED', 'ASSIGNED', 'OUT_FOR_DELIVERY'].includes(p.status));
      if (!includeUnscheduled) return open(ofDay);
      const all = await parcelsApi.list({ limit: 300 });
      const undated = all.filter((p) => !p.scheduledDate);
      const known = new Set(ofDay.map((p) => p.id));
      return open([...ofDay, ...undated.filter((p) => !known.has(p.id))]);
    },
    enabled: hasVrpModule && hasPod,
  });
  // Localizable parcels: already geocoded, or with an address we can geocode.
  const localizableParcels = useMemo(
    () => (parcels ?? []).filter((p) => (p.recipientLat != null && p.recipientLng != null) || !!p.recipientAddress),
    [parcels],
  );

  const { data: tours } = useQuery({
    queryKey: ['vrp-tours', date],
    queryFn: () => vrpApi.listTours({ from: date, to: date }),
    enabled: hasVrpModule,
  });

  // Mission IDs already used in existing tours for this date — those should
  // not be selectable again (would lead to duplicate planning).
  const alreadyPlannedMissionIds = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    for (const t of tours ?? []) {
      for (const s of t.stops) {
        if (!s.isBreak && s.missionId) set.add(s.missionId);
      }
    }
    return set;
  }, [tours]);

  const alreadyPlannedParcelIds = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    for (const t of tours ?? []) {
      for (const s of t.stops) {
        if (!s.isBreak && s.parcelId) set.add(s.parcelId);
      }
    }
    return set;
  }, [tours]);

  // Missions visible in the picker depend on which drivers are checked:
  // - 0 driver checked → show everything (the dispatcher hasn't picked yet)
  // - 1+ driver(s) checked → show missions assigned to one of them, plus the
  //   unassigned pool when `includeUnassigned` is on. Missions assigned to a
  //   driver that is NOT in the selection are hidden.
  const visibleMissions = useMemo(() => {
    if (selectedDriverIds.size === 0) return geocodedMissions;
    return geocodedMissions.filter((m) => {
      if (m.assignedDriverId == null) return includeUnassigned;
      return selectedDriverIds.has(m.assignedDriverId);
    });
  }, [geocodedMissions, selectedDriverIds, includeUnassigned]);

  // When the visible set shrinks, drop any selected mission that is no longer
  // visible — otherwise we'd silently send hidden missions to the optimizer.
  useEffect(() => {
    setSelectedMissionIds((prev) => {
      if (prev.size === 0) return prev;
      const visibleIds = new Set(visibleMissions.map((m) => m.id));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (visibleIds.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [visibleMissions]);

  // Parcels visible in the picker — same per-driver rule as missions.
  const visibleParcels = useMemo(() => {
    if (selectedDriverIds.size === 0) return localizableParcels;
    return localizableParcels.filter((p) => {
      if (p.assignedDriverId == null) return includeUnassigned;
      return selectedDriverIds.has(p.assignedDriverId);
    });
  }, [localizableParcels, selectedDriverIds, includeUnassigned]);

  useEffect(() => {
    setSelectedParcelIds((prev) => {
      if (prev.size === 0) return prev;
      const visibleIds = new Set(visibleParcels.map((p) => p.id));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (visibleIds.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [visibleParcels]);

  const [tourStopOrder, setTourStopOrder] = useState<Record<string, string[]>>({});
  useEffect(() => {
    if (!tours) return;
    const next: Record<string, string[]> = {};
    for (const t of tours) next[t.id] = t.stops.map((s) => s.id);
    setTourStopOrder(next);
  }, [tours]);

  const optimizeMutation = useMutation({
    mutationFn: vrpApi.optimize,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['vrp-tours', date] });
      setErrorMsg(null);
      const w = data.warnings;
      if (w && (w.failedRefs.length > 0 || w.approximateRefs.length > 0)) {
        const parts: string[] = [];
        if (w.failedRefs.length > 0) {
          parts.push(t('tour_optim.warn_failed_refs', { refs: w.failedRefs.join(', ') }));
        }
        if (w.approximateRefs.length > 0) {
          parts.push(t('tour_optim.warn_approximate_refs', { refs: w.approximateRefs.join(', ') }));
        }
        setWarnMsg(parts.join(' · '));
      } else {
        setWarnMsg(null);
      }
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? (err as Error).message;
      setErrorMsg(msg);
      setWarnMsg(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: vrpApi.deleteTour,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vrp-tours', date] }),
  });

  const updateDeviceMutation = useMutation({
    mutationFn: (input: { id: string; data: Partial<DeviceLite> }) =>
      devicesApi.update(input.id, input.data as never),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vrp-drivers'] }),
  });

  // Description and phone aren't carried by the TourStop schema, so we fold
  // them into the label/address we send. That way the driver still sees the
  // full context on their stop card.
  function manualToStop(s: ManualStop) {
    return {
      label: s.description ? `${s.label} — ${s.description}` : s.label,
      latitude: s.latitude,
      longitude: s.longitude,
      address: s.contactPhone ? `${s.address ?? ''}${s.address ? ' · ' : ''}☎ ${s.contactPhone}` : s.address,
      serviceDurationS: s.serviceDurationS,
      twStart: s.twStart,
      twEnd: s.twEnd,
      demand: s.demand,
      requiredSkills: s.requiredSkills,
    };
  }

  function buildOptimizeBody(date: string, vehicles: VrpOptimizeRequest['vehicles']): VrpOptimizeRequest {
    // All three sources are merged into a single optimize call. The source
    // toggle is just a UI navigation aid (which form is currently visible) —
    // a dispatcher can mix selected missions + a pasted CSV + ad-hoc relais
    // in the same optimization run.
    const stops: NonNullable<VrpOptimizeRequest['stops']> = [];
    const missionIds: string[] = [];

    // Missions
    const selectedMissions = geocodedMissions.filter((m) => selectedMissionIds.has(m.id));
    for (const m of selectedMissions) {
      const ov = missionOverrides[m.id] ?? {};
      if (m.latitude != null && m.longitude != null) {
        stops.push({
          missionId: m.id,
          label: `${m.reference} — ${m.title}`,
          latitude: m.latitude,
          longitude: m.longitude,
          address: m.address ?? undefined,
          serviceDurationS: ov.serviceDurationS ?? (m.estimatedDurationMin ? m.estimatedDurationMin * 60 : 0),
          twStart: ov.twStart,
          twEnd: ov.twEnd,
          demand: ov.demand,
          requiredSkills: ov.requiredSkills,
        });
      } else {
        missionIds.push(m.id);
      }
    }

    // CSV — only consume if the textarea has content.
    if (csvText.trim().length > 0) {
      const { stops: csvStops } = parseCsv(csvText);
      for (const s of csvStops) {
        stops.push({
          label: s.label, latitude: s.latitude, longitude: s.longitude, address: s.address,
          serviceDurationS: s.serviceDurationS, twStart: s.twStart, twEnd: s.twEnd,
          demand: s.demand, requiredSkills: s.requiredSkills,
        });
      }
    }

    // Manual relais
    for (const s of manualStops) stops.push(manualToStop(s));

    return {
      date,
      stops,
      missionIds: missionIds.length > 0 ? missionIds : undefined,
      vehicles,
    };
  }

  // Turn the selected parcels into optimizer stops. Parcels missing coordinates
  // are forward-geocoded from their recipient address; un-geolocatable ones are
  // skipped (reported via a warning). Each stop carries `parcelId` so the tour
  // save writes back the parcel's scheduled date + driver.
  async function resolveParcelStops(): Promise<{ stops: NonNullable<VrpOptimizeRequest['stops']>; skipped: number }> {
    const selected = (parcels ?? []).filter(
      (p) => selectedParcelIds.has(p.id) && !alreadyPlannedParcelIds.has(p.id),
    );
    const stops: NonNullable<VrpOptimizeRequest['stops']> = [];
    let skipped = 0;
    for (const p of selected) {
      let lat = p.recipientLat;
      let lng = p.recipientLng;
      if (lat == null || lng == null) {
        const addr = [p.recipientAddress, p.recipientPostalCode, p.recipientCity, p.recipientCountry]
          .filter(Boolean).join(', ');
        if (addr) {
          try {
            const r = await geocodingApi.search(addr, { limit: 1 });
            if (r[0]) { lat = r[0].latitude; lng = r[0].longitude; }
          } catch { /* leave null → skipped below */ }
        }
      }
      if (lat == null || lng == null) { skipped += 1; continue; }
      stops.push({
        parcelId: p.id,
        label: `${p.reference}${p.recipientName ? ` — ${p.recipientName}` : ''}`,
        latitude: lat,
        longitude: lng,
        address: [p.recipientAddress, p.recipientPostalCode, p.recipientCity].filter(Boolean).join(', ') || undefined,
        demand: p.weightKg != null ? Math.max(0, Math.round(p.weightKg)) : undefined,
      });
    }
    return { stops, skipped };
  }

  async function runOptimize() {
    setErrorMsg(null);
    const chosen = drivers.filter((d) => selectedDriverIds.has(d.driverId));
    if (chosen.length === 0) { setErrorMsg(t('tour_optim.err_select_driver')); return; }
    const noVehicle = chosen.find((d) => !d.vehicle);
    if (noVehicle) { setErrorMsg(t('tour_optim.err_no_vehicle', { name: noVehicle.driverName })); return; }
    const noPos = chosen.find((d) => d.effectiveDepot.lat == null || d.effectiveDepot.lng == null);
    if (noPos) { setErrorMsg(t('tour_optim.err_no_depot', { name: noPos.driverName })); return; }

    const shiftStart = (() => {
      const t = (startTime && /^\d{2}:\d{2}$/.test(startTime)) ? startTime : '08:00';
      return new Date(`${date}T${t}:00`).toISOString();
    })();
    const shiftEnd = new Date(`${date}T23:59:00`).toISOString();

    const vehicles = chosen.map((d) => ({
      vehicleId: d.vehicle!.id,
      driverId: d.driverId,
      startLatitude: d.effectiveDepot.lat!,
      startLongitude: d.effectiveDepot.lng!,
      capacity: d.vehicle!.capacityKg ?? undefined,
      skills: d.vehicle!.skills.length > 0 ? d.vehicle!.skills : undefined,
      shiftStart,
      shiftEnd,
    }));

    const body = buildOptimizeBody(date, vehicles);
    const { stops: parcelStops, skipped } = await resolveParcelStops();
    if (parcelStops.length > 0) body.stops = [...(body.stops ?? []), ...parcelStops];
    const stopCount = (body.stops?.length ?? 0) + (body.missionIds?.length ?? 0);
    if (stopCount === 0) { setErrorMsg(t('tour_optim.err_no_stops')); return; }
    if (skipped > 0) setWarnMsg(t('tour_optim.parcels_skipped', { count: skipped }));
    optimizeMutation.mutate(body);
  }

  /**
   * Existing tour stops may be split into multiple TourStop rows when a forced
   * break falls in the middle of a mission's service (label suffix `(1/2)`,
   * `(2/2)`, …). Resending them as N distinct stops to VROOM would make the
   * optimizer think the mission has to be visited N times — and the backend
   * re-injects breaks on each chunk, producing extra duplicates. Collapse
   * split mission rows into a single stop with the cumulative service time
   * before re-optimizing.
   */
  function dedupeTourStops(stops: VrpStop[]) {
    type OptStop = {
      missionId?: string;
      label?: string;
      latitude: number;
      longitude: number;
      address?: string;
      serviceDurationS: number;
      twStart?: string;
      twEnd?: string;
    };
    const out: OptStop[] = [];
    const idxByMission = new Map<string, number>();
    for (const s of stops) {
      if (s.isBreak) continue;
      const cleanLabel = s.label?.replace(/\s*\(\d+\/\d+\)\s*$/, '') ?? undefined;
      if (s.missionId) {
        const idx = idxByMission.get(s.missionId);
        if (idx != null) {
          out[idx]!.serviceDurationS += s.serviceDurationS;
          continue;
        }
        idxByMission.set(s.missionId, out.length);
      }
      out.push({
        missionId: s.missionId ?? undefined,
        label: cleanLabel,
        latitude: s.latitude,
        longitude: s.longitude,
        address: s.address ?? undefined,
        serviceDurationS: s.serviceDurationS,
        twStart: s.twStart ?? undefined,
        twEnd: s.twEnd ?? undefined,
      });
    }
    return out;
  }

  async function recalcLockedTour(tour: VrpTour) {
    const order = tourStopOrder[tour.id];
    if (!order) return;
    const seqStops = order
      .map((sid) => tour.stops.find((s) => s.id === sid))
      .filter((x): x is NonNullable<typeof x> => Boolean(x));

    const stops = dedupeTourStops(seqStops);
    if (stops.length === 0) {
      setErrorMsg(t('tour_optim.err_no_remaining_stops'));
      return;
    }

    if (!tour.vehicleId || tour.startLatitude == null || tour.startLongitude == null) return;

    const shiftStart = (() => {
      const t = (startTime && /^\d{2}:\d{2}$/.test(startTime)) ? startTime : '08:00';
      return new Date(`${date}T${t}:00`).toISOString();
    })();
    const shiftEnd = new Date(`${date}T23:59:00`).toISOString();

    const body: VrpOptimizeRequest = {
      date,
      stops,
      vehicles: [{
        vehicleId: tour.vehicleId,
        driverId: tour.driverId ?? undefined,
        startLatitude: tour.startLatitude,
        startLongitude: tour.startLongitude,
        endLatitude: tour.endLatitude ?? undefined,
        endLongitude: tour.endLongitude ?? undefined,
        shiftStart,
        shiftEnd,
        lockedSequenceIdx: stops.map((_, i) => i),
      }],
    };
    setErrorMsg(null);
    try {
      await deleteMutation.mutateAsync(tour.id);
      await optimizeMutation.mutateAsync(body);
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? (err as Error).message
        ?? t('tour_optim.err_recalc_default');
      setErrorMsg(msg);
    }
  }

  /**
   * Insert a manual relais into an existing tour: rebuild the tour with
   * [existing non-break stops + the new manual stop], delete the old tour,
   * and let VROOM place the new stop optimally. Removes the manual stop from
   * the local list once the new tour is created.
   */
  async function appendManualStopToTour(tour: VrpTour, manual: ManualStop) {
    if (!tour.vehicleId || tour.startLatitude == null || tour.startLongitude == null) {
      setErrorMsg(t('tour_optim.err_no_vehicle_or_depot'));
      return;
    }
    // Collapse split-mission rows so VROOM doesn't see the same mission twice.
    const existingStops = dedupeTourStops(tour.stops);

    const shiftStart = (() => {
      const t = (startTime && /^\d{2}:\d{2}$/.test(startTime)) ? startTime : '08:00';
      return new Date(`${date}T${t}:00`).toISOString();
    })();
    const shiftEnd = new Date(`${date}T23:59:00`).toISOString();

    const body: VrpOptimizeRequest = {
      date,
      stops: [...existingStops, manualToStop(manual)],
      vehicles: [{
        vehicleId: tour.vehicleId,
        driverId: tour.driverId ?? undefined,
        startLatitude: tour.startLatitude,
        startLongitude: tour.startLongitude,
        endLatitude: tour.endLatitude ?? undefined,
        endLongitude: tour.endLongitude ?? undefined,
        shiftStart,
        shiftEnd,
      }],
    };
    setErrorMsg(null);
    // Chain via mutateAsync — nesting `onSuccess` callbacks inside `mutate()`
    // proved unreliable: the second mutation occasionally never fired after
    // the first one's global onSuccess (queryClient.invalidateQueries) had
    // returned its own promise.
    try {
      await deleteMutation.mutateAsync(tour.id);
      await optimizeMutation.mutateAsync(body);
      setManualStops((prev) => prev.filter((s) => s.id !== manual.id));
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? (err as Error).message
        ?? 'Erreur lors de l\'insertion du relais';
      setErrorMsg(msg);
    }
  }

  // Map
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);

  useEffect(() => {
    if (!hasVrpModule) return;
    if (!mapContainerRef.current || mapRef.current) return;
    mapRef.current = new maplibregl.Map({
      container: mapContainerRef.current,
      style: {
        version: 8,
        sources: {
          'raster-tiles': {
            type: 'raster',
            tiles: [
              'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
              'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
              'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
            ],
            tileSize: 256,
            attribution: '&copy; OpenStreetMap',
          },
        },
        layers: [{ id: 'raster-layer', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 19 }],
      },
      center: DEFAULT_CENTER,
      zoom: 6,
    });
    mapRef.current.addControl(new maplibregl.NavigationControl(), 'top-right');
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, [hasVrpModule]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !tours) return;
    if (!map.isStyleLoaded()) {
      map.once('load', () => renderTours(map, tours));
      return;
    }
    renderTours(map, tours);
  }, [tours]);

  // Resize the map after the container height transitions (collapse/expand)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const t = setTimeout(() => map.resize(), 220);
    return () => clearTimeout(t);
  }, [mapCollapsed]);

  // Click-on-map mode: when picking a depot, the next map click sets it.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!pickingDepotForVehicleId) {
      map.getCanvas().style.cursor = '';
      return;
    }
    map.getCanvas().style.cursor = 'crosshair';
    const handler = (e: maplibregl.MapMouseEvent) => {
      const id = pickingDepotForVehicleId;
      if (!id) return;
      updateDeviceMutation.mutate({
        id,
        data: { depotLatitude: e.lngLat.lat, depotLongitude: e.lngLat.lng },
      });
      setPickingDepotForVehicleId(null);
    };
    map.once('click', handler);
    return () => {
      map.off('click', handler);
      map.getCanvas().style.cursor = '';
    };
  }, [pickingDepotForVehicleId, updateDeviceMutation]);

  if (!hasVrpModule) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-800">
          <h2 className="font-semibold mb-1">{t('tour_optim.module_off_title')}</h2>
          <p className="text-sm">{t('tour_optim.module_off_body')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Route className="h-6 w-6 sm:h-7 sm:w-7 text-primary-600" />
          {t('tour_optim.title')}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {t('tour_optim.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: configuration */}
        <div className="lg:col-span-1 space-y-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('tour_optim.date')}</label>
              <input
                type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('tour_optim.start_time')}</label>
              <input
                type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <DriversSection
            drivers={drivers}
            selectedIds={selectedDriverIds}
            onToggle={(id) => setSelectedDriverIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; })}
            openId={openDriverId}
            onOpen={setOpenDriverId}
            onSaveVehicle={(vehicleId, data) => updateDeviceMutation.mutate({ id: vehicleId, data })}
            onPickOnMap={(vehicleId) => {
              setPickingDepotForVehicleId(vehicleId);
              setOpenDriverId(null);
            }}
          />

          <StopsSection
            source={stopSource} onSourceChange={setStopSource}
            missions={visibleMissions}
            alreadyPlannedMissionIds={alreadyPlannedMissionIds}
            selectedMissionIds={selectedMissionIds}
            onToggleMission={(id) => setSelectedMissionIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; })}
            hasPod={hasPod}
            parcels={visibleParcels}
            alreadyPlannedParcelIds={alreadyPlannedParcelIds}
            selectedParcelIds={selectedParcelIds}
            onToggleParcel={(id) => setSelectedParcelIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; })}
            missionOverrides={missionOverrides}
            onMissionOverride={(id, patch) => setMissionOverrides((p) => ({ ...p, [id]: { ...(p[id] ?? {}), ...patch } }))}
            csvText={csvText} onCsvChange={setCsvText}
            manualStops={manualStops} onManualChange={setManualStops}
            openStopId={openStopId} onOpenStop={setOpenStopId}
            includeUnscheduled={includeUnscheduled}
            onToggleUnscheduled={setIncludeUnscheduled}
            includeUnassigned={includeUnassigned}
            onToggleUnassigned={setIncludeUnassigned}
            anyDriverSelected={selectedDriverIds.size > 0}
            multiDriver={selectedDriverIds.size >= 2}
            existingTours={(tours ?? []).filter((t) => t.status === 'PLANNED')}
            onAppendStopToTour={(tourId, stop) => {
              const tour = (tours ?? []).find((t) => t.id === tourId);
              if (tour) appendManualStopToTour(tour, stop);
            }}
            isAppending={optimizeMutation.isPending || deleteMutation.isPending}
          />

          {errorMsg && (
            <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 p-3 text-sm">
              {errorMsg}
            </div>
          )}
          {warnMsg && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 p-3 text-sm">
              {warnMsg}
            </div>
          )}

          <button
            onClick={() => void runOptimize()}
            disabled={optimizeMutation.isPending}
            className="w-full px-4 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium flex items-center justify-center gap-2 shadow-sm"
          >
            {optimizeMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('tour_optim.optimize')}
          </button>
        </div>

        {/* Right: map + tours list + Gantt */}
        <div className="lg:col-span-2 space-y-4">
          {pickingDepotForVehicleId && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 text-blue-800 px-4 py-3 text-sm flex items-center gap-2">
              <MousePointerClick className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">
                {t('tour_optim.click_map_for_depot_before')}{' '}
                <strong>
                  {drivers.find((d) => d.vehicle?.id === pickingDepotForVehicleId)?.vehicle?.name ?? t('tour_optim.this_vehicle')}
                </strong>.
              </span>
              <button
                onClick={() => setPickingDepotForVehicleId(null)}
                className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-100"
                title={t('tour_optim.cancel')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <div className="relative">
            <div
              ref={mapContainerRef}
              className={`w-full ${mapCollapsed ? 'h-[140px]' : 'h-[500px]'} rounded-lg overflow-hidden border border-gray-200 shadow-sm transition-[height] duration-200`}
            />
            <button
              type="button"
              onClick={() => setMapCollapsed((v) => !v)}
              className="absolute top-2 left-2 z-10 px-2 py-1 rounded-md bg-white/95 hover:bg-white border border-gray-200 shadow-sm text-xs text-gray-700 flex items-center gap-1"
              title={mapCollapsed ? t('tour_optim.expand_map') : t('tour_optim.collapse_map')}
            >
              {mapCollapsed ? <Maximize2 className="w-3.5 h-3.5" /> : <Minimize2 className="w-3.5 h-3.5" />}
              {mapCollapsed ? t('tour_optim.expand') : t('tour_optim.collapse')}
            </button>
          </div>

          {tours && tours.length > 0 && <GanttChart tours={tours} />}

          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              {t('tour_optim.tours_for_date', { date, count: tours?.length ?? 0 })}
            </h2>
            {tours && tours.length === 0 && (
              <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
                {t('tour_optim.no_tour_for_date')}
              </div>
            )}
            {(tours ?? []).map((t, idx) => (
              <TourCard
                key={t.id} tour={t} idx={idx}
                stopOrder={tourStopOrder[t.id] ?? t.stops.map((s) => s.id)}
                onReorder={(newOrder) => setTourStopOrder((p) => ({ ...p, [t.id]: newOrder }))}
                onDeleteStop={(stopId) =>
                  setTourStopOrder((p) => ({
                    ...p,
                    [t.id]: (p[t.id] ?? t.stops.map((s) => s.id)).filter((id) => id !== stopId),
                  }))
                }
                isOrderChanged={
                  (tourStopOrder[t.id] ?? t.stops.map((s) => s.id)).join(',')
                    !== t.stops.map((s) => s.id).join(',')
                }
                onRecalc={() => recalcLockedTour(t)}
                onDelete={() => deleteMutation.mutate(t.id)}
                isBusy={optimizeMutation.isPending || deleteMutation.isPending}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function DriversSection({ drivers, selectedIds, onToggle, openId, onOpen, onSaveVehicle, onPickOnMap }: {
  drivers: DriverPickItem[];
  selectedIds: Set<string>;
  onToggle: (driverId: string) => void;
  openId: string | null;
  onOpen: (id: string | null) => void;
  onSaveVehicle: (vehicleId: string, data: Partial<DeviceLite>) => void;
  onPickOnMap: (vehicleId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Truck className="w-4 h-4 text-gray-500" /> {t('tour_optim.drivers_section')}
          <span className="ml-auto text-xs font-normal text-gray-500">{selectedIds.size}/{drivers.length}</span>
        </h2>
      </div>
      <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
        {drivers.length === 0 ? (
          <p className="text-sm text-gray-500 p-4">Aucun conducteur.</p>
        ) : drivers.map((d) => {
          const noVehicle = !d.vehicle;
          const noPos = !noVehicle && (d.effectiveDepot.lat == null || d.effectiveDepot.lng == null);
          const blocked = noVehicle || noPos;
          const isOpen = openId === d.driverId;
          return (
            <div key={d.driverId}>
              <div className="flex items-start gap-2 px-4 py-2.5 hover:bg-gray-50">
                <input
                  type="checkbox" disabled={blocked} checked={selectedIds.has(d.driverId)} onChange={() => onToggle(d.driverId)}
                  className="mt-0.5 rounded border-gray-300 text-primary-500 focus:ring-primary-500/20"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 truncate">{d.driverName}</div>
                  {d.vehicle ? (
                    <div className="text-xs text-gray-500 truncate flex items-center gap-1">
                      <Truck className="w-3 h-3" />
                      <span>{d.vehicle.name}</span>
                      {d.vehicle.plate && <span className="font-mono">· {d.vehicle.plate}</span>}
                      {d.vehicle.capacityKg != null && d.vehicle.capacityKg > 0 && (
                        <span>· {d.vehicle.capacityKg} kg</span>
                      )}
                      {d.vehicle.skills.length > 0 && (
                        <span className="truncate">· {d.vehicle.skills.join(', ')}</span>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-amber-600">{t('tour_optim.no_vehicle_assigned')}</div>
                  )}
                </div>
                {noPos && (
                  <span title={t('tour_optim.tooltip_no_position')}
                    className="text-amber-500 mt-0.5"
                  >
                    <AlertTriangle className="w-3.5 h-3.5" />
                  </span>
                )}
                {d.vehicle && (
                  <button
                    onClick={() => onOpen(isOpen ? null : d.driverId)}
                    className="text-gray-400 hover:text-gray-700 p-1 rounded hover:bg-gray-100 mt-0.5"
                    title={t('tour_optim.tooltip_configure_vehicle')}
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                )}
              </div>
              {isOpen && d.vehicle && (
                <VehicleEditor
                  vehicle={d.vehicle}
                  onSave={(data) => onSaveVehicle(d.vehicle!.id, data)}
                  onPickOnMap={() => onPickOnMap(d.vehicle!.id)}
                />
              )}
            </div>
          );
        })}
      </div>
      <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 rounded-b-lg">
        {t('tour_optim.vehicle_provides_depot')}
      </div>
    </div>
  );
}

function VehicleEditor({ vehicle, onSave, onPickOnMap }: {
  vehicle: NonNullable<VrpDriver['vehicle']>;
  onSave: (data: Partial<DeviceLite>) => void;
  onPickOnMap: () => void;
}) {
  const { t } = useTranslation();
  const [lat, setLat] = useState<string>(vehicle.depotLatitude?.toString() ?? '');
  const [lng, setLng] = useState<string>(vehicle.depotLongitude?.toString() ?? '');
  const [cap, setCap] = useState<string>(vehicle.capacityKg?.toString() ?? '');
  const [skillsText, setSkillsText] = useState<string>(vehicle.skills.join(', '));

  // Address autocomplete (forward-geocoding via Nominatim)
  const [addressQuery, setAddressQuery] = useState<string>('');
  const [results, setResults] = useState<ForwardGeocodingResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Debounced search
  useEffect(() => {
    const q = addressQuery.trim();
    if (q.length < 3) { setResults([]); return; }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const r = await geocodingApi.search(q, { limit: 5 });
        setResults(r);
        setShowResults(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [addressQuery]);

  function pickResult(r: ForwardGeocodingResult) {
    setLat(r.latitude.toFixed(6));
    setLng(r.longitude.toFixed(6));
    setAddressQuery(r.displayName);
    setShowResults(false);
  }

  function save() {
    onSave({
      depotLatitude: lat ? Number(lat) : null,
      depotLongitude: lng ? Number(lng) : null,
      capacityKg: cap ? Number(cap) : null,
      skills: skillsText.split(',').map((s) => s.trim()).filter(Boolean),
    });
  }

  return (
    <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 space-y-3">
      {/* Mode 1 — address search */}
      <div className="relative">
        <label className="block text-xs font-medium text-gray-700 mb-1">{t('tour_optim.field_depot_address')}</label>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            value={addressQuery}
            onChange={(e) => setAddressQuery(e.target.value)}
            onFocus={() => results.length > 0 && setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 150)}
            placeholder="12 rue de Rivoli, Paris"
            className={`${inputClassSm} pl-7`}
          />
          {searching && (
            <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin" />
          )}
        </div>
        {showResults && results.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
            {results.map((r, i) => (
              <li key={`${r.latitude}-${r.longitude}-${i}`}>
                <button
                  onMouseDown={(e) => { e.preventDefault(); pickResult(r); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-100 last:border-0"
                >
                  <div className="text-gray-900 truncate">{r.displayName}</div>
                  <div className="text-gray-400 font-mono">{r.latitude.toFixed(4)}, {r.longitude.toFixed(4)}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Mode 2 — pick on map */}
      <button
        type="button"
        onClick={onPickOnMap}
        className="w-full px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium flex items-center justify-center gap-2"
      >
        <MousePointerClick className="w-3.5 h-3.5" /> Choisir sur la carte
      </button>

      {/* Mode 3 — manual lat/lng (also reflects modes 1 & 2) */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">{t('tour_optim.field_gps_coords')}</label>
        <div className="grid grid-cols-2 gap-2">
          <input value={lat} onChange={(e) => setLat(e.target.value)} placeholder="Latitude" inputMode="decimal" className={inputClassSm} />
          <input value={lng} onChange={(e) => setLng(e.target.value)} placeholder="Longitude" inputMode="decimal" className={inputClassSm} />
        </div>
      </div>

      <div className="border-t border-gray-200 pt-3 space-y-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('tour_optim.field_capacity_kg')}</label>
          <input value={cap} onChange={(e) => setCap(e.target.value)} placeholder="1000" inputMode="numeric" className={inputClassSm} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('tour_optim.field_skills_csv')}</label>
          <input value={skillsText} onChange={(e) => setSkillsText(e.target.value)} placeholder="frigo, hayon" className={inputClassSm} />
        </div>
      </div>

      <button onClick={save} className="w-full px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium">
        {t('tour_optim.save')}
      </button>
    </div>
  );
}

function StopsSection(props: {
  source: StopSource;
  onSourceChange: (s: StopSource) => void;
  missions: Array<{
    id: string; reference: string; title: string;
    address: string | null;
    latitude: number | null; longitude: number | null;
    estimatedDurationMin?: number | null;
    scheduledAt?: string | null;
    status?: string;
    assignedDriverId?: string | null;
    assignedDriver?: { firstName: string; lastName: string } | null;
  }>;
  alreadyPlannedMissionIds: Set<string>;
  selectedMissionIds: Set<string>;
  onToggleMission: (id: string) => void;
  hasPod: boolean;
  parcels: Array<{
    id: string; reference: string; recipientName: string | null; status: string;
    recipientAddress: string | null; recipientCity: string | null; recipientPostalCode: string | null;
    recipientLat: number | null; recipientLng: number | null;
    assignedDriverId: string | null;
    assignedDriver?: { firstName: string; lastName: string } | null;
  }>;
  alreadyPlannedParcelIds: Set<string>;
  selectedParcelIds: Set<string>;
  onToggleParcel: (id: string) => void;
  missionOverrides: Record<string, Partial<ManualStop>>;
  onMissionOverride: (id: string, patch: Partial<ManualStop>) => void;
  csvText: string;
  onCsvChange: (text: string) => void;
  manualStops: ManualStop[];
  onManualChange: (s: ManualStop[]) => void;
  openStopId: string | null;
  onOpenStop: (id: string | null) => void;
  includeUnscheduled: boolean;
  onToggleUnscheduled: (v: boolean) => void;
  includeUnassigned: boolean;
  onToggleUnassigned: (v: boolean) => void;
  anyDriverSelected: boolean;
  multiDriver: boolean;
  existingTours: VrpTour[];
  onAppendStopToTour: (tourId: string, stop: ManualStop) => void;
  isAppending: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-gray-500" /> {t('tour_optim.section_stops')}
        </h2>
      </div>
      <div className="px-4 pt-3">
        <div className="inline-flex w-full rounded-lg border border-gray-200 bg-gray-50 p-1">
          {[
            { k: 'missions' as const, label: 'Missions', icon: ListChecks },
            ...(props.hasPod ? [{ k: 'parcels' as const, label: 'Colis', icon: PackageCheck }] : []),
            { k: 'csv' as const, label: 'CSV', icon: Upload },
            { k: 'manual' as const, label: 'Manuel', icon: Plus },
          ].map((t) => (
            <button key={t.k} onClick={() => props.onSourceChange(t.k)}
              className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                props.source === t.k ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {props.source === 'missions' && (
        <>
          <div className="px-4 pt-3 pb-1 space-y-1.5">
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={props.includeUnscheduled}
                onChange={(e) => props.onToggleUnscheduled(e.target.checked)}
                className="rounded border-gray-300 text-primary-500 focus:ring-primary-500/20"
              />
              {t('tour_optim.filter_include_undated')}
            </label>
            {props.anyDriverSelected && (
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={props.includeUnassigned}
                  onChange={(e) => props.onToggleUnassigned(e.target.checked)}
                  className="rounded border-gray-300 text-primary-500 focus:ring-primary-500/20"
                />
                {t('tour_optim.filter_include_unassigned')}
              </label>
            )}
            {props.anyDriverSelected && (
              <p className="text-[10px] text-gray-400 leading-snug">
                {props.multiDriver
                  ? t('tour_optim.filter_help_multi')
                  : t('tour_optim.filter_help_single')}
              </p>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 mt-2">
            {props.missions.length === 0 ? (
              <p className="text-sm text-gray-500 p-4">{t('tour_optim.no_geocoded_missions')}</p>
            ) : props.missions.map((m) => {
            const isOpen = props.openStopId === m.id;
            const ov = props.missionOverrides[m.id] ?? {};
            const alreadyPlanned = props.alreadyPlannedMissionIds.has(m.id);
            return (
              <div key={m.id} className={alreadyPlanned ? 'opacity-60' : ''}>
                <div className="flex items-start gap-2 px-4 py-2.5 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    disabled={alreadyPlanned}
                    title={alreadyPlanned ? t('tour_optim.tooltip_already_planned') : undefined}
                    checked={!alreadyPlanned && props.selectedMissionIds.has(m.id)}
                    onChange={() => !alreadyPlanned && props.onToggleMission(m.id)}
                    className="mt-1 rounded border-gray-300 text-primary-500 focus:ring-primary-500/20 disabled:cursor-not-allowed"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900 truncate flex items-center gap-1.5">
                      <span className="truncate">{m.reference} — {m.title}</span>
                      {m.status && <MissionStatusBadge status={m.status} />}
                      {(m.latitude == null || m.longitude == null) && (
                        <span
                          title={t('tour_optim.tooltip_to_geocode')}
                          className="text-[10px] px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-100 flex-shrink-0"
                        >
                          {t('tour_optim.badge_to_geocode')}
                        </span>
                      )}
                      {props.multiDriver && m.assignedDriver && (
                        <span
                          title={t('tour_optim.tooltip_locked_on', { name: `${m.assignedDriver.firstName} ${m.assignedDriver.lastName}` })}
                          className="text-[10px] px-1.5 py-0.5 rounded border bg-violet-50 text-violet-700 border-violet-100 flex-shrink-0"
                        >
                          → {m.assignedDriver.firstName}
                        </span>
                      )}
                      {props.anyDriverSelected && !m.assignedDriverId && (
                        <span
                          title={t('tour_optim.tooltip_unassigned')}
                          className="text-[10px] px-1.5 py-0.5 rounded border bg-gray-50 text-gray-600 border-gray-200 flex-shrink-0"
                        >
                          Libre
                        </span>
                      )}
                      {alreadyPlanned && (
                        <span
                          title={t('tour_optim.tooltip_already_planned_short')}
                          className="text-[10px] px-1.5 py-0.5 rounded border bg-gray-100 text-gray-600 border-gray-200 flex-shrink-0"
                        >
                          {t('tour_optim.badge_already_planned')}
                        </span>
                      )}
                    </div>
                    {m.address && <div className="text-xs text-gray-500 truncate">{m.address}</div>}
                  </div>
                  <button onClick={() => props.onOpenStop(isOpen ? null : m.id)} className="text-gray-400 hover:text-gray-700 p-1 rounded hover:bg-gray-100">
                    <Settings2 className="w-4 h-4" />
                  </button>
                </div>
                {isOpen && (
                  <StopOverridesEditor
                    initial={ov}
                    defaultServiceMin={m.estimatedDurationMin ?? undefined}
                    onSave={(patch) => { props.onMissionOverride(m.id, patch); props.onOpenStop(null); }}
                  />
                )}
              </div>
            );
          })}
          </div>
        </>
      )}

      {props.source === 'parcels' && (
        <>
          <div className="px-4 pt-3 pb-1 space-y-1.5">
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={props.includeUnscheduled}
                onChange={(e) => props.onToggleUnscheduled(e.target.checked)}
                className="rounded border-gray-300 text-primary-500 focus:ring-primary-500/20"
              />
              {t('tour_optim.filter_include_undated')}
            </label>
            {props.anyDriverSelected && (
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={props.includeUnassigned}
                  onChange={(e) => props.onToggleUnassigned(e.target.checked)}
                  className="rounded border-gray-300 text-primary-500 focus:ring-primary-500/20"
                />
                {t('tour_optim.filter_include_unassigned')}
              </label>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 mt-2">
            {props.parcels.length === 0 ? (
              <p className="text-sm text-gray-500 p-4">{t('tour_optim.no_parcels')}</p>
            ) : props.parcels.map((p) => {
              const alreadyPlanned = props.alreadyPlannedParcelIds.has(p.id);
              const noCoords = p.recipientLat == null || p.recipientLng == null;
              return (
                <div key={p.id} className={`flex items-start gap-2 px-4 py-2.5 hover:bg-gray-50 ${alreadyPlanned ? 'opacity-60' : ''}`}>
                  <input
                    type="checkbox"
                    disabled={alreadyPlanned}
                    checked={!alreadyPlanned && props.selectedParcelIds.has(p.id)}
                    onChange={() => !alreadyPlanned && props.onToggleParcel(p.id)}
                    className="mt-1 rounded border-gray-300 text-primary-500 focus:ring-primary-500/20 disabled:cursor-not-allowed"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900 truncate flex items-center gap-1.5">
                      <span className="truncate">{p.reference}{p.recipientName ? ` — ${p.recipientName}` : ''}</span>
                      {noCoords && (
                        <span title={t('tour_optim.tooltip_to_geocode')} className="text-[10px] px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-100 flex-shrink-0">
                          {t('tour_optim.badge_to_geocode')}
                        </span>
                      )}
                      {props.multiDriver && p.assignedDriver && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded border bg-violet-50 text-violet-700 border-violet-100 flex-shrink-0">
                          → {p.assignedDriver.firstName}
                        </span>
                      )}
                      {props.anyDriverSelected && !p.assignedDriverId && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded border bg-gray-50 text-gray-600 border-gray-200 flex-shrink-0">Libre</span>
                      )}
                      {alreadyPlanned && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded border bg-gray-100 text-gray-600 border-gray-200 flex-shrink-0">
                          {t('tour_optim.badge_already_planned')}
                        </span>
                      )}
                    </div>
                    {(p.recipientAddress || p.recipientCity) && (
                      <div className="text-xs text-gray-500 truncate">
                        {[p.recipientAddress, p.recipientPostalCode, p.recipientCity].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {props.source === 'csv' && (
        <div className="p-4 space-y-2">
          <p className="text-xs text-gray-500">
            <span dangerouslySetInnerHTML={{ __html: t('tour_optim.csv_format_help') }} />
          </p>
          <textarea
            value={props.csvText} onChange={(e) => props.onCsvChange(e.target.value)} rows={8}
            placeholder={`Client A,12 rue X Paris,48.8566,2.3522,15,2026-04-26T09:00:00Z,2026-04-26T11:00:00Z,50,frigo|hayon`}
            className="w-full text-xs font-mono px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
          />
          <div className="text-xs text-gray-500">
            {(() => {
              const r = parseCsv(props.csvText);
              const base = t('tour_optim.csv_parsed_count', { count: r.stops.length });
              return r.errors.length ? base + t('tour_optim.csv_with_errors', { count: r.errors.length, first: r.errors[0] }) : base;
            })()}
          </div>
        </div>
      )}

      {props.source === 'manual' && (
        <div className="p-4">
          <ManualStopsEditor
            stops={props.manualStops}
            onChange={props.onManualChange}
            existingTours={props.existingTours}
            onAppendToTour={props.onAppendStopToTour}
            isAppending={props.isAppending}
          />
        </div>
      )}
    </div>
  );
}

function StopOverridesEditor({ initial, defaultServiceMin, onSave }: {
  initial: Partial<ManualStop>; defaultServiceMin?: number;
  onSave: (patch: Partial<ManualStop>) => void;
}) {
  const { t } = useTranslation();
  const [twStart, setTwStart] = useState<string>(initial.twStart ?? '');
  const [twEnd, setTwEnd] = useState<string>(initial.twEnd ?? '');
  const [serviceMin, setServiceMin] = useState<string>(
    initial.serviceDurationS != null ? String(Math.round(initial.serviceDurationS / 60)) : (defaultServiceMin?.toString() ?? '')
  );
  const [demand, setDemand] = useState<string>(initial.demand?.toString() ?? '');
  const [skills, setSkills] = useState<string>((initial.requiredSkills ?? []).join(', '));

  function save() {
    onSave({
      twStart: twStart || undefined,
      twEnd: twEnd || undefined,
      serviceDurationS: serviceMin ? Number(serviceMin) * 60 : undefined,
      demand: demand ? Number(demand) : undefined,
      requiredSkills: skills.split(',').map((s) => s.trim()).filter(Boolean),
    });
  }

  return (
    <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('tour_optim.field_tw_start')}</label>
          <input value={twStart} onChange={(e) => setTwStart(e.target.value)} placeholder="2026-04-26T09:00:00Z" className={inputClassSm} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('tour_optim.field_tw_end')}</label>
          <input value={twEnd} onChange={(e) => setTwEnd(e.target.value)} placeholder="2026-04-26T11:00:00Z" className={inputClassSm} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Service (min)</label>
          <input value={serviceMin} onChange={(e) => setServiceMin(e.target.value)} inputMode="numeric" className={inputClassSm} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Demande (kg)</label>
          <input value={demand} onChange={(e) => setDemand(e.target.value)} inputMode="numeric" className={inputClassSm} />
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-gray-500 mb-1">{t('tour_optim.field_skills_required')}</label>
          <input value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="frigo, hayon" className={inputClassSm} />
        </div>
      </div>
      <button onClick={save} className="w-full px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium">
        {t('tour_optim.save')}
      </button>
    </div>
  );
}

function ManualStopsEditor({
  stops, onChange,
  existingTours, onAppendToTour, isAppending,
}: {
  stops: ManualStop[];
  onChange: (s: ManualStop[]) => void;
  existingTours: VrpTour[];
  onAppendToTour: (tourId: string, stop: ManualStop) => void;
  isAppending: boolean;
}) {
  const { t } = useTranslation();
  // The dispatcher fills natural fields (title, address parts, contact). The
  // backend forward-geocoder resolves coords on click — no GPS input needed.
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [street, setStreet] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('France');
  const [contactPhone, setContactPhone] = useState('');
  const [serviceMin, setServiceMin] = useState('');
  const [demand, setDemand] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Per-stop "append to tour" picker — null = closed, otherwise the stop id
  // whose target picker is currently open.
  const [pickingTourForStopId, setPickingTourForStopId] = useState<string | null>(null);

  function reset() {
    setTitle('');
    setDescription('');
    setStreet('');
    setPostalCode('');
    setCity('');
    setCountry('France');
    setContactPhone('');
    setServiceMin('');
    setDemand('');
    setErrorMsg(null);
  }

  async function add() {
    setErrorMsg(null);
    if (!title.trim()) { setErrorMsg('Le titre est obligatoire.'); return; }
    if (!street.trim() && !city.trim() && !postalCode.trim()) {
      setErrorMsg('Indiquez au moins une adresse, un code postal ou une ville.');
      return;
    }
    const composed = [street, postalCode, city, country].map((p) => p.trim()).filter(Boolean).join(', ');
    setBusy(true);
    try {
      // Progressive fallback: the full query first, then drop the postal code
      // if it doesn't match Nominatim, then drop the country, etc. Mirrors the
      // logic the backend uses for mission geocoding so the dispatcher gets
      // the best chance of placing the relais on the map.
      const variants = [
        composed,
        [street, city, country].map((p) => p.trim()).filter(Boolean).join(', '),
        [street, city].map((p) => p.trim()).filter(Boolean).join(', '),
        [postalCode, city, country].map((p) => p.trim()).filter(Boolean).join(', '),
        [city, country].map((p) => p.trim()).filter(Boolean).join(', '),
      ].filter((q, i, arr) => q && arr.indexOf(q) === i);

      let hit: ForwardGeocodingResult | null = null;
      for (const q of variants) {
        try {
          const res = await geocodingApi.search(q, { limit: 1 });
          if (res.length > 0) { hit = res[0]!; break; }
        } catch {
          /* try next variant */
        }
      }
      if (!hit) {
        setErrorMsg(t('tour_optim.err_address_not_found'));
        return;
      }

      const niceAddress = composed || hit.displayName;
      const stop: ManualStop = {
        id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        label: title.trim(),
        description: description.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        latitude: hit.latitude,
        longitude: hit.longitude,
        address: niceAddress,
        serviceDurationS: serviceMin ? Math.max(0, Math.round(Number(serviceMin) * 60)) : undefined,
        demand: demand ? Math.max(0, Math.floor(Number(demand))) : undefined,
      };
      onChange([...stops, stop]);
      reset();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Titre du relais <span className="text-red-500">*</span></label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Livraison client A"
          className={inputClassSm}
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder={t('tour_optim.field_driver_instruction')}
          className={`${inputClassSm} resize-y`}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-xs font-medium text-gray-700">Adresse</label>
        <input
          value={street}
          onChange={(e) => setStreet(e.target.value)}
          placeholder="N° et rue (ex: 12 rue de Rivoli)"
          className={inputClassSm}
        />
        <div className="grid grid-cols-3 gap-2">
          <input
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            placeholder="Code postal"
            className={inputClassSm}
          />
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Ville"
            className={`${inputClassSm} col-span-2`}
          />
        </div>
        <input
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          placeholder="Pays"
          className={inputClassSm}
        />
        <p className="text-[10px] text-gray-400">
          {t('tour_optim.gps_help_auto')}
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">{t('tour_optim.field_contact_phone')}</label>
        <input
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value)}
          placeholder="06 12 34 56 78"
          className={inputClassSm}
        />
      </div>

      <button
        type="button"
        onClick={() => setAdvancedOpen((v) => !v)}
        className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1"
      >
        {advancedOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {t('tour_optim.advanced_options')}
      </button>
      {advancedOpen && (
        <div className="grid grid-cols-2 gap-2 pl-3 border-l-2 border-gray-100">
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Service (min)</label>
            <input
              inputMode="numeric"
              value={serviceMin}
              onChange={(e) => setServiceMin(e.target.value)}
              placeholder="15"
              className={inputClassSm}
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Demande (kg)</label>
            <input
              inputMode="numeric"
              value={demand}
              onChange={(e) => setDemand(e.target.value)}
              placeholder="50"
              className={inputClassSm}
            />
          </div>
        </div>
      )}

      {errorMsg && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 px-2 py-1.5 text-xs">
          {errorMsg}
        </div>
      )}

      <button
        onClick={add}
        disabled={busy}
        className="w-full px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium flex items-center justify-center gap-1"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
        {busy ? 'Recherche de l\'adresse…' : 'Ajouter le relais'}
      </button>

      {stops.length > 0 && (
        <div className="mt-2 space-y-1 max-h-72 overflow-y-auto">
          {stops.map((s, i) => {
            const isPicking = pickingTourForStopId === s.id;
            return (
              <div key={s.id} className="rounded bg-gray-50 border border-gray-200 text-sm text-gray-900">
                <div className="flex items-start gap-2 px-2 py-1.5">
                  <span className="text-gray-400 w-5 text-right pt-0.5">{i + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{s.label}</div>
                    {s.address && <div className="text-xs text-gray-500 truncate">{s.address}</div>}
                    {s.description && <div className="text-[11px] text-gray-500 truncate italic">{s.description}</div>}
                    {s.contactPhone && <div className="text-[11px] text-gray-500 truncate">☎ {s.contactPhone}</div>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setPickingTourForStopId(isPicking ? null : s.id)}
                      disabled={isAppending}
                      title={t('tour_optim.tooltip_insert_in_tour')}
                      className="text-[10px] px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 disabled:opacity-50 flex items-center gap-1"
                    >
                      <Route className="w-3 h-3" />
                      {t('tour_optim.btn_insert_tour')}
                    </button>
                    <button
                      onClick={() => onChange(stops.filter((x) => x.id !== s.id))}
                      className="text-gray-400 hover:text-red-500 p-1"
                      title="Supprimer ce relais"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {isPicking && (
                  <div className="border-t border-gray-200 bg-white px-2 py-2 space-y-1.5">
                    <div className="text-[11px] text-gray-500">
                      {t('tour_optim.insert_tour_help')}
                    </div>
                    {existingTours.length === 0 ? (
                      <div className="text-xs text-gray-500 italic px-1 py-1">
                        {t('tour_optim.no_tours_planned_for_insert')}
                      </div>
                    ) : (
                      <ul className="space-y-1">
                        {existingTours.map((tour) => {
                          const driverLabel = tour.driver
                            ? `${tour.driver.firstName} ${tour.driver.lastName}`.trim()
                            : t('tour_optim.unassigned_short');
                          const vehicleLabel = tour.vehicle?.name ?? '';
                          return (
                            <li key={tour.id}>
                              <button
                                type="button"
                                onClick={() => {
                                  onAppendToTour(tour.id, s);
                                  setPickingTourForStopId(null);
                                }}
                                disabled={isAppending}
                                className="w-full text-left px-2 py-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-50 flex items-center justify-between gap-2"
                              >
                                <span className="truncate">
                                  <span className="font-medium">{driverLabel}</span>
                                  {vehicleLabel && <span className="text-gray-500"> · {vehicleLabel}</span>}
                                </span>
                                <span className="text-[10px] text-gray-500 flex-shrink-0">
                                  {t('tour_optim.stops_count_short', { count: tour.stops.filter((st) => !st.isBreak).length })}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    <button
                      type="button"
                      onClick={() => setPickingTourForStopId(null)}
                      className="text-[11px] text-gray-500 hover:text-gray-800 underline"
                    >
                      Annuler
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tour card with DnD reorder
// ---------------------------------------------------------------------------

function TourCard({
  tour, idx, stopOrder, onReorder, onDeleteStop, isOrderChanged, onRecalc, onDelete, isBusy,
}: {
  tour: VrpTour; idx: number;
  stopOrder: string[];
  onReorder: (newOrder: string[]) => void;
  onDeleteStop: (stopId: string) => void;
  isOrderChanged: boolean;
  onRecalc: () => void;
  onDelete: () => void;
  isBusy: boolean;
}) {
  const { t } = useTranslation();
  const dragSrcId = useRef<string | null>(null);

  const orderedStops = useMemo(
    () => stopOrder.map((sid) => tour.stops.find((s) => s.id === sid)).filter((x): x is NonNullable<typeof x> => Boolean(x)),
    [stopOrder, tour.stops],
  );

  function handleDragStart(stopId: string) { dragSrcId.current = stopId; }
  function handleDrop(targetId: string) {
    const src = dragSrcId.current; dragSrcId.current = null;
    if (!src || src === targetId) return;
    const cur = [...stopOrder];
    const fromIdx = cur.indexOf(src);
    const toIdx = cur.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    cur.splice(fromIdx, 1);
    cur.splice(toIdx, 0, src);
    onReorder(cur);
  }

  const color = TOUR_COLORS[idx % TOUR_COLORS.length];

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-2 p-4 border-b border-gray-100">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
          <div className="min-w-0">
            <div className="text-gray-900 font-medium truncate">
              {tour.driver
                ? `${tour.driver.firstName} ${tour.driver.lastName}`.trim() || 'Conducteur sans nom'
                : t('tour_optim.no_driver_assigned')}
            </div>
            {tour.vehicle && (
              <div className="text-xs text-gray-500 truncate flex items-center gap-1">
                <Truck className="w-3 h-3" />
                <span>{tour.vehicle.name}</span>
                {tour.vehicle.plate && <span className="font-mono">· {tour.vehicle.plate}</span>}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isOrderChanged && (
            <button
              onClick={onRecalc} disabled={isBusy}
              className="text-xs px-2 py-1 rounded-md bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white flex items-center gap-1 font-medium"
              title={t('tour_optim.tooltip_recalc_eta')}
            >
              <RefreshCw className="w-3 h-3" /> Recalculer
            </button>
          )}
          <button
            onClick={onDelete}
            className="text-gray-400 hover:text-red-500 p-1.5 rounded hover:bg-gray-100"
            title={t('tour_optim.tooltip_delete_tour')}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
        {(() => {
          const breakCount = tour.stops.filter((s) => s.isBreak).length;
          const stopCount = tour.stops.length - breakCount;
          const breakSec = tour.stops.filter((s) => s.isBreak).reduce((acc, s) => acc + s.serviceDurationS, 0);
          // tour.totalDurationS = travel + breaks (VROOM duration + injected breaks).
          // True wall-clock total = travel + service + breaks.
          const driveSec = Math.max(0, tour.totalDurationS - breakSec);
          const wallClockSec = driveSec + tour.totalServiceS + breakSec;
          return (
            <>
              <span dangerouslySetInnerHTML={{ __html: t('tour_optim.stops_count', { count: stopCount }).replace(`${stopCount}`, `<strong class="text-gray-900">${stopCount}</strong>`) }} />
              {breakCount > 0 && <span><strong className="text-gray-900">{breakCount}</strong> pause{breakCount > 1 ? 's' : ''}</span>}
              <span><strong className="text-gray-900">{formatDistance(tour.totalDistanceM)}</strong></span>
              <span title="Total = trajet + service + pauses"><strong className="text-gray-900">{formatDuration(wallClockSec)}</strong> total</span>
              <span className="text-gray-400">→ {formatDuration(driveSec)} trajet · {formatDuration(tour.totalServiceS)} service{breakCount > 0 ? ` · ${formatDuration(breakSec)} pause` : ''}</span>
            </>
          );
        })()}
      </div>
      <ol className="divide-y divide-gray-100">
        {orderedStops.map((s, i) => {
          if (s.isBreak) {
            return (
              <li key={s.id} className="flex items-center gap-2 px-4 py-2 text-sm bg-amber-50/40 text-amber-900">
                <span className="w-3 h-3 flex-shrink-0" />
                <span className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
                  <Coffee className="w-3 h-3" />
                </span>
                <span className="flex-1 truncate">Pause obligatoire</span>
                <span className="text-xs text-amber-700 font-medium flex-shrink-0">{formatDuration(s.serviceDurationS)}</span>
                {s.plannedArrival && (
                  <span className="text-xs text-amber-700 font-mono flex-shrink-0">
                    {new Date(s.plannedArrival).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </li>
            );
          }
          return (
            <li
              key={s.id}
              draggable
              onDragStart={() => handleDragStart(s.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(s.id)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-move"
            >
              <GripVertical className="w-3 h-3 text-gray-300 flex-shrink-0" />
              <span
                className="w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: color }}
              >{i + 1}</span>
              <span className={`flex-1 truncate ${s.mission?.status === 'COMPLETED' ? 'line-through text-gray-400' : ''}`}>
                {s.label ?? s.address ?? `${s.latitude.toFixed(4)}, ${s.longitude.toFixed(4)}`}
              </span>
              {s.mission?.status && <MissionStatusBadge status={s.mission.status} />}
              {s.serviceDurationS > 0 && (
                <span className="text-xs text-gray-500 flex-shrink-0">{formatDuration(s.serviceDurationS)}</span>
              )}
              {s.demand != null && s.demand > 0 && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100 flex-shrink-0">{s.demand} kg</span>
              )}
              {s.requiredSkills && s.requiredSkills.length > 0 && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-100 flex-shrink-0">{s.requiredSkills.join(',')}</span>
              )}
              {s.plannedArrival && (
                <span className="text-xs text-gray-500 font-mono flex-shrink-0">
                  {new Date(s.plannedArrival).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDeleteStop(s.id); }}
                className="text-gray-300 hover:text-red-500 p-1 rounded hover:bg-gray-100 flex-shrink-0"
                title={t('tour_optim.tooltip_remove_stop')}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gantt chart (SVG)
// ---------------------------------------------------------------------------

function GanttChart({ tours }: { tours: VrpTour[] }) {
  const { t: tr } = useTranslation();
  const allTimes = tours.flatMap((t) => t.stops.flatMap((s) => {
    const a = s.plannedArrival ? new Date(s.plannedArrival).getTime() : null;
    const d = s.plannedDeparture ? new Date(s.plannedDeparture).getTime() : a;
    return [a, d].filter((x): x is number => Boolean(x));
  }));
  if (allTimes.length === 0) return null;
  const tMin = Math.min(...allTimes);
  const tMax = Math.max(...allTimes);
  const total = Math.max(60_000, tMax - tMin);

  const rowHeight = 28;
  const labelBoxWidth = 150;       // visible grey box for the driver name
  const labelGutter = 24;          // breathing space between label box and gantt
  const labelWidth = labelBoxWidth + labelGutter;
  const width = 820;
  const trackWidth = width - labelWidth - 16;
  const height = tours.length * rowHeight + 36;

  function xFor(ts: number) {
    return labelWidth + ((ts - tMin) / total) * trackWidth;
  }

  const ticks: number[] = [];
  const hourStart = new Date(tMin); hourStart.setMinutes(0, 0, 0);
  for (let t = hourStart.getTime(); t <= tMax + 3600_000; t += 3600_000) {
    if (t >= tMin) ticks.push(t);
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-x-auto">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-gray-900">{tr('tour_optim.gantt_title')}</h3>
        <div className="flex gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-2 rounded-sm" style={{ backgroundColor: '#06b6d4', opacity: 0.85 }} />
            Service
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-2 rounded-sm" style={{ backgroundColor: '#06b6d4', opacity: 0.25 }} />
            Trajet
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-2 rounded-sm" style={{ backgroundColor: '#f59e0b', opacity: 0.7 }} />
            Pause
          </span>
        </div>
      </div>
      <div className="p-3">
        <svg width={width} height={height} className="block">
          {ticks.map((t) => {
            const x = xFor(t);
            return (
              <g key={t}>
                <line x1={x} y1={24} x2={x} y2={height - 4} stroke="#e5e7eb" strokeWidth={1} />
                <text x={x} y={16} fill="#6b7280" fontSize={10} textAnchor="middle">
                  {new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </text>
              </g>
            );
          })}
          {tours.map((t, idx) => {
            const y = 28 + idx * rowHeight;
            const color = TOUR_COLORS[idx % TOUR_COLORS.length];
            const label = t.driver
              ? `${t.driver.firstName} ${t.driver.lastName}`.trim() || 'Conducteur'
              : (t.vehicle?.name ?? translate('tour_optim.fallback_tour_n', { n: idx + 1 }));

            // Build travel segments = gaps between consecutive stops, plus the
            // initial leg from the shift start to the very first stop.
            const stopsWithTimes = t.stops
              .filter((s) => s.plannedArrival && s.plannedDeparture)
              .sort((a, b) => new Date(a.plannedArrival!).getTime() - new Date(b.plannedArrival!).getTime());
            const travelSegs: Array<{ start: number; end: number }> = [];
            const first = stopsWithTimes[0];
            if (first) {
              const firstArrival = new Date(first.plannedArrival!).getTime();
              // shiftStart = arrival of first stop − cumulative travel duration to it
              const shiftStart = firstArrival - (first.arrivalDurationS ?? 0) * 1000;
              if (firstArrival > shiftStart) travelSegs.push({ start: shiftStart, end: firstArrival });
            }
            for (let i = 1; i < stopsWithTimes.length; i++) {
              const prev = stopsWithTimes[i - 1]!;
              const cur = stopsWithTimes[i]!;
              const prevDep = new Date(prev.plannedDeparture!).getTime();
              const curArr = new Date(cur.plannedArrival!).getTime();
              if (curArr > prevDep) travelSegs.push({ start: prevDep, end: curArr });
            }

            return (
              <g key={t.id}>
                <rect x={0} y={y} width={labelBoxWidth} height={rowHeight - 4} fill="#f9fafb" rx={4} />
                <circle cx={12} cy={y + rowHeight / 2 - 2} r={4} fill={color} />
                <text x={22} y={y + rowHeight / 2 + 3} fill="#111827" fontSize={11} fontWeight={500}>
                  {label.length > 18 ? label.slice(0, 16) + '…' : label}
                </text>
                {/* Travel segments — light tour color, behind stops */}
                {travelSegs.map((seg, si) => {
                  const x = xFor(seg.start);
                  const w = Math.max(1, xFor(seg.end) - x);
                  const minutes = Math.max(1, Math.round((seg.end - seg.start) / 60_000));
                  return (
                    <g key={`travel-${si}`}>
                      <rect
                        x={x} y={y + 8} width={w} height={rowHeight - 20}
                        fill={color} fillOpacity={0.25} rx={2}
                      />
                      <title>{`Trajet ${minutes} min · ${new Date(seg.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} → ${new Date(seg.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}</title>
                    </g>
                  );
                })}
                {t.stops.map((s) => {
                  if (!s.plannedArrival) return null;
                  const a = new Date(s.plannedArrival).getTime();
                  const d = s.plannedDeparture ? new Date(s.plannedDeparture).getTime() : a + 60_000;
                  const x = xFor(a);
                  const w = Math.max(2, xFor(d) - x);
                  const fill = s.isBreak ? '#f59e0b' : color;
                  const minutes = Math.max(1, Math.round(s.serviceDurationS / 60));
                  const showLabel = w >= 32;
                  return (
                    <g key={s.id}>
                      <rect
                        x={x} y={y + 4} width={w} height={rowHeight - 12}
                        fill={fill} fillOpacity={s.isBreak ? 0.7 : 0.85}
                        stroke={s.isBreak ? '#d97706' : 'none'} strokeWidth={s.isBreak ? 1 : 0}
                        rx={3}
                      />
                      {showLabel && (
                        <text
                          x={x + w / 2} y={y + rowHeight / 2 + 2}
                          fill="white" fontSize={9} fontWeight={600} textAnchor="middle"
                          style={{ pointerEvents: 'none' }}
                        >
                          {s.isBreak ? '☕' : `${minutes}m`}
                        </text>
                      )}
                      <title>{`${s.isBreak ? 'Pause' : `${s.sequence}. ${s.label ?? s.address ?? ''}`}\n${new Date(a).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} → ${new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} (${minutes} min)`}</title>
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Map rendering
// ---------------------------------------------------------------------------

function renderTours(map: MapLibreMap, tours: VrpTour[]) {
  const style = map.getStyle();
  for (const layer of style.layers ?? []) {
    if (layer.id.startsWith('vrp-tour-')) map.removeLayer(layer.id);
  }
  for (const sourceId of Object.keys(style.sources ?? {})) {
    if (sourceId.startsWith('vrp-tour-')) map.removeSource(sourceId);
  }
  document.querySelectorAll('.vrp-stop-marker').forEach((el) => el.remove());

  if (!tours || tours.length === 0) return;
  const allCoords: Array<[number, number]> = [];
  tours.forEach((tour, idx) => {
    const color = TOUR_COLORS[idx % TOUR_COLORS.length];
    const polyline = tour.geometry?.polyline;
    let coords: Array<[number, number]> = [];
    if (polyline) coords = decodePolyline(polyline);
    if (coords.length > 0) {
      const sourceId = `vrp-tour-${tour.id}`;
      map.addSource(sourceId, { type: 'geojson', data: { type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: {} } });
      map.addLayer({ id: `vrp-tour-line-${tour.id}`, type: 'line', source: sourceId, paint: { 'line-color': color, 'line-width': 4, 'line-opacity': 0.85 } });
      allCoords.push(...coords);
    }
    // Visible job stops only; breaks are timeline-only and would just stack on
    // the previous stop's pin.
    let visibleSeq = 0;
    tour.stops.forEach((s) => {
      if (s.isBreak) { allCoords.push([s.longitude, s.latitude]); return; }
      visibleSeq += 1;
      const el = document.createElement('div');
      el.className = 'vrp-stop-marker';
      el.style.cssText = `width:22px;height:22px;border-radius:50%;background:${color};color:white;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3)`;
      el.textContent = String(visibleSeq);
      new maplibregl.Marker({ element: el })
        .setLngLat([s.longitude, s.latitude])
        .setPopup(new maplibregl.Popup({ offset: 16 }).setHTML(
          `<div style="font-family:sans-serif;font-size:12px"><b>${visibleSeq}.</b> ${(s.label ?? s.address ?? '').replace(/[<>]/g, '')}${
            s.plannedArrival
              ? `<br/><span style="color:#666">${translate('tour_optim.popup_arrival', { time: new Date(s.plannedArrival).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}</span>`
              : ''
          }</div>`,
        ))
        .addTo(map);
      allCoords.push([s.longitude, s.latitude]);
    });
  });
  if (allCoords.length > 0) {
    const lngs = allCoords.map((c) => c[0]);
    const lats = allCoords.map((c) => c[1]);
    map.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      { padding: 50, duration: 600 },
    );
  }
}
